import * as React from 'react';
import { RadioImage } from '@origin-digital/style-guide';
import { Box } from '@origin-digital/layout';
import analytics from '../../../../analytics';
import FieldError from '../../Layout/FieldError';
import { RadioGroupGrid } from '../../../RadioGroupGrid';
import { ClickableLink } from '../../../Link';
import { CustomerDetails } from '../../../../redux/actions/customer';
import { FieldArrayRenderProps } from 'formik';

export type RadioImageGroupFieldProps = {
  name: string;
  touched?: boolean;
  options: { [option: string]: any }[];
  maxOptionsToShow?: number;
  helpers: FieldArrayRenderProps;
  isDisqualified?: boolean;
  changeHandler?: (payload: CustomerDetails) => void;
  disqualificationHandler?: (field: string, form: any) => void;
};

export type RadioImageGroupFieldState = {
  showAllOptions: boolean;
};

function getOptionsToShow(
  options: { [option: string]: any }[],
  maxOptionsToShow: number,
  showAllOptions: boolean
): { [option: string]: any }[] {
  if (options.length <= maxOptionsToShow || showAllOptions === true) {
    return options;
  }
  return options.slice(0, maxOptionsToShow);
}

export class RadioImageGroupField extends React.Component<
  RadioImageGroupFieldProps,
  RadioImageGroupFieldState
> {
  constructor(props: RadioImageGroupFieldProps) {
    super(props);

    this.state = {
      showAllOptions: false
    };
  }

  public componentWillMount() {
    const { helpers, options } = this.props;
    const visibleOptions = options.slice(0, 4);
    const visibleOptionLabels = visibleOptions.map(i =>
      i.label
        .replace(/ /g, '_')
        .replace(/-/g, '')
        .toUpperCase()
    );

    if (
      helpers.form.values.roofType !== undefined &&
      !visibleOptionLabels.includes(helpers.form.values.roofType)
    ) {
      this.setState({
        showAllOptions: true
      });
    }
  }

  public componentDidUpdate() {
    const { disqualificationHandler, helpers, name } = this.props;

    if (disqualificationHandler) {
      disqualificationHandler(name, helpers.form);
    }
  }

  public render() {
    const { helpers, name, options, maxOptionsToShow = 4, isDisqualified } = this.props;
    const { showAllOptions } = this.state;
    const hasError = (helpers.form.errors[name] && !isDisqualified) || false;
    const value = helpers.form.values[name];
    const optionsToShow = getOptionsToShow(options, maxOptionsToShow, showAllOptions);
    const canHideItems = options.length > maxOptionsToShow;

    return (
      <React.Fragment>
        <RadioGroupGrid name={name} onChange={e => this.onSelect(e.value)} value={value}>
          {optionsToShow.map((o, i) => (
            <RadioImage key={i} name={o.value} label={o.label} src={o.src} />
          ))}
        </RadioGroupGrid>

        {canHideItems ? (
          <Box marginBottom={4}>
            <ClickableLink
              text={showAllOptions ? 'Show less' : 'Show more'}
              color="red"
              onClick={() => this.toggleShowAllOptions()}
            />
          </Box>
        ) : null}

        {hasError && (
          <Box marginLeft={3}>
            <FieldError name={name} error={name} />
          </Box>
        )}
      </React.Fragment>
    );
  }

  private onSelect = (value: string): void => {
    const { helpers, name, changeHandler } = this.props;

    if (helpers.form.values[name] !== value) {
      helpers.form.setFieldValue(name, value, true);
      helpers.form.setFieldTouched(name, true, true);

      if (changeHandler) {
        changeHandler({ [name]: value });
      }
    }

    analytics.radioSelect(name, value);
  };

  private toggleShowAllOptions = () => {
    this.setState({
      showAllOptions: !this.state.showAllOptions
    });
  };
}
